import is from '@sindresorhus/is';
import type { QueryClient } from '@tanstack/react-query';

import type { ContextRefreshEvent } from '../../../data-contract/chat';
import { invalidateContextRefreshQueries } from '../../api/use-context-refresh-invalidation';
import { recordEntityChanges } from '../../stores/entity-change-store';
import { recordEntityCreations } from '../../stores/entity-creation-store';
import { offerVersionRedirects } from '../stores/version-redirect-store';

export type ContextRefreshEffects = {
    /**
     * Applies a live context refresh: re-reads what the agent changed, lets mounted cards glimmer for
     * updated versions, lets a mounted surface open what the agent created, and offers updated items
     * the user is looking at for a version redirect. Callers run it once per event id.
     */
    readonly apply: (refresh: ContextRefreshEvent) => void;
    /** Cancels the follow-up reads still scheduled; the owner can keep applying afterwards. */
    readonly dispose: () => void;
};

export const createContextRefreshEffects = (queryClient: QueryClient): ContextRefreshEffects => {
    const timers = new Set<number>();
    const schedule = (run: () => void, delay: number): void => {
        const timer = window.setTimeout(() => {
            timers.delete(timer);
            run();
        }, delay);
        timers.add(timer);
    };

    return {
        apply: (refresh) => {
            const arrivedAt = Date.now();
            invalidateContextRefreshQueries(refresh.entities, queryClient, schedule);

            if (refresh.action === 'updated') {
                recordEntityChanges(
                    refresh.id,
                    refresh.entities.flatMap((entity) =>
                        is.nonEmptyString(entity.id)
                            ? [{ entityId: entity.id, version: is.number(entity.version) ? entity.version : null }]
                            : [],
                    ),
                );
                offerVersionRedirects(refresh.id, refresh.entities, arrivedAt);
            }

            if (refresh.action === 'created') {
                recordEntityCreations(
                    refresh.id,
                    refresh.entities.map((entity) => ({
                        architectureId: is.nonEmptyString(entity.jobArchitectureId) ? entity.jobArchitectureId : null,
                        createdAt: arrivedAt,
                        type: entity.type,
                    })),
                );
            }
        },
        dispose: () => {
            for (const timer of timers) window.clearTimeout(timer);
            timers.clear();
        },
    };
};
